import * as React from 'react';

import { createColumnHelper } from '@tanstack/react-table';
import { useIntl } from 'react-intl';

import { useListApiKeys } from 'src/features/apiKeys/api/useListApiKeys';
import CreateApiKeyFormModal from 'src/features/apiKeys/components/forms/CreateApiKeyFormModal';
import CellApiKeyActions from 'src/features/apiKeys/components/tables/CellApiKeyActions';
import { apiKeysMessages } from 'src/features/apiKeys/utils/apiKeysMessages';
import { ApiKey } from 'src/features/apiKeys/utils/types';
import { PERMISSION } from 'src/features/auth/types';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { useParking } from 'src/providers/ParkingProvider';
import { CellDatetime } from 'src/registry/components/table';
import { DataTable } from 'src/registry/components/table/DataTable';
import DataTableColumnHeader from 'src/registry/components/table/DataTableColumnHeader';
import { DataTableControls } from 'src/registry/components/table/DataTableControls';
import { DataTablePagination } from 'src/registry/components/table/DataTablePagination';
import usePagination from 'src/registry/hooks/usePagination';
import useSorting from 'src/registry/hooks/useSorting';
import { useTitleStore } from 'src/stores/useTitleStore';
import { commonMessages } from 'src/utils/messages';

const sortingOptions = {
    initialSortingState: [{ id: 'created', desc: true }],
};

const columnHelper = createColumnHelper<ApiKey>();

function ApiKeys() {
    const { formatMessage } = useIntl();
    const { parkingId } = useParking();

    const manageApiKeys = useHasPermissions([PERMISSION.MANAGE_API_KEYS]);

    const { querySortingOptions, ...sorting } = useSorting(sortingOptions);
    const { queryPaginationOptions, ...pagination } = usePagination();

    const { data, isLoading } = useListApiKeys({
        parkingId,
        ...querySortingOptions,
        ...queryPaginationOptions,
    });

    const { setTitle, removeTitle } = useTitleStore();

    React.useEffect(() => {
        setTitle(formatMessage(apiKeysMessages.apiKeys));
        return removeTitle;
    }, [formatMessage, removeTitle, setTitle]);

    const columns = [
        columnHelper.accessor('prefix', {
            header: DataTableColumnHeader,
            enableSorting: true,
            meta: {
                title: formatMessage(apiKeysMessages.prefix),
            },
        }),
        columnHelper.accessor('name', {
            header: DataTableColumnHeader,
            enableSorting: true,
            meta: {
                title: formatMessage(commonMessages.name),
            },
        }),
        columnHelper.accessor('created', {
            header: DataTableColumnHeader,
            enableSorting: true,
            cell: CellDatetime,
            meta: {
                title: formatMessage(apiKeysMessages.createdAtApiKey),
            },
        }),
        ...(manageApiKeys
            ? [
                  columnHelper.display({
                      id: 'actions',
                      header: DataTableColumnHeader,
                      cell: CellApiKeyActions,
                      maxSize: 45,
                  }),
              ]
            : []),
    ];

    return (
        <DataTable<ApiKey, any>
            isLoading={isLoading}
            columns={columns}
            data={data}
            sorting={sorting}
            pagination={pagination}
            semantics={{
                title: formatMessage(apiKeysMessages.apiKeys),
                entryName: formatMessage(apiKeysMessages.apiKey).toLowerCase(),
                entryNames: formatMessage(apiKeysMessages.apiKeys).toLowerCase(),
            }}
            components={{
                controls: DataTableControls,
                pagination: DataTablePagination,
                ...(manageApiKeys && { newEntry: CreateApiKeyFormModal }),
            }}
        />
    );
}

export default ApiKeys;
